import React, {
    useEffect,
    useState,
    useCallback,
    useMemo,
} from 'react';
import { observer } from 'mobx-react';
import { useParams, useNavigate } from 'react-router';
import {
    Button,
    Upload,
    message,
    Tag,
    Popconfirm,
    Alert,
} from 'antd';
import {
    CloseOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    SyncOutlined,
    ExclamationCircleOutlined,
    ReloadOutlined,
    WarningOutlined,
} from '@ant-design/icons';

import TabTitle from '@/components/Common/TabTitle';
import Spinner from '@/components/Common/Spin';
import { Input, Password } from '@/components/Common/UIKit';
import { useStores } from '@/components/hooks';
import i18n from '@/content';
import { AppRoutes } from '@/config/appRoutes';
import { captureErrorForSentry } from '@/components/utils';
import { PUBLIC_ASSETS } from '@/consts';

import parseJsonFile from '../fileParcer';
import type { IntegrationType, ConnectorType } from '@/stores/SettingsStore/interfaces';
import { CONNECTOR_TYPES } from '@/stores/SettingsStore';
import { SlackInlineWizard, SlackCredentials } from '../SlackSetupWizard';
import { SharePointInlineWizard, SharePointCredentials } from '../SharePointSetupWizard';

import styles from './index.module.scss';

const INTEGRATION_META: Record<IntegrationType, { category: string; logo: string }> = {
    sharepoint: { category: 'Storage Systems', logo: `${PUBLIC_ASSETS}/icons/sharepoint-logo.svg` },
    gcs: { category: 'Storage Systems', logo: `${PUBLIC_ASSETS}/icons/google-cloud-logo.svg` },
    slack: { category: 'Connectors', logo: `${PUBLIC_ASSETS}/icons/slack-logo.svg` },
    salesforce: { category: 'Connectors', logo: `${PUBLIC_ASSETS}/icons/salesforce-logo.svg` },
    purview: { category: 'Classification & Governance', logo: `${PUBLIC_ASSETS}/icons/purview-logo.svg` },
    sharepoint_connector: { category: 'Connectors', logo: `${PUBLIC_ASSETS}/icons/sharepoint-logo.svg` },
};

/**
 * Drill-down page for configuring a specific integration.
 * Accessed via /Settings/integration/:type
 */
const IntegrationConfigPage: React.FC = observer(() => {
    const { type } = useParams<{ type: string }>();
    const navigate = useNavigate();
    const { settingsStore, organizationsStore } = useStores();
    const {
        isLoading,
        fetchConfiguredIntegrations,
        fetchStorageKeys,
        saveIntegration,
        getIntegrationFields,
        getIntegrationValues,
        fetchConnectorHealth,
        fetchConnectorManifest,
        connectorHealthStatus,
        getConnectorUrl,
        fetchPurviewLabels,
        syncPurviewLabels,
        purviewLabels,
        isPurviewLabelsLoading,
        isPurviewRescanning,
    } = settingsStore;

    const [values, setValues] = useState<Record<string, string>>({});
    const [initialValues, setInitialValues] = useState<Record<string, string>>({});
    const [jsonFileName, setJsonFileName] = useState<string>('');
    const [isSaving, setIsSaving] = useState(false);
    const [isResetting, setIsResetting] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [manifestJson, setManifestJson] = useState<string | null>(null);
    const [showPurviewInfo, setShowPurviewInfo] = useState(false);

    const integrationType = type as IntegrationType;
    const nameSpace = 'settings';

    // Check if this is a connector type with health endpoint
    const isConnector = CONNECTOR_TYPES.includes(integrationType as ConnectorType);
    const connectorType = isConnector ? (integrationType as ConnectorType) : null;
    const hasHealthEndpoint = connectorType && !!getConnectorUrl(connectorType);
    const healthStatus = connectorType ? connectorHealthStatus[connectorType] : null;
    const isPurviewIntegration = integrationType === 'purview';
    const integrationMeta = INTEGRATION_META[integrationType];

    /** Check if a value is a real configured value (not empty or masked placeholder) */
    const isRealValue = (val: string | undefined): boolean => {
        if (!val) return false;
        const trimmed = val.trim();
        // Empty string or single asterisk (masked placeholder) means not configured
        return trimmed !== '' && trimmed !== '*';
    };

    /** Check if ALL Purview fields are fully configured on the server (initial load).
     *  Based on initialValues (stable) rather than values (reactive to typing). */
    const isPurviewFullyConfiguredOnServer = useMemo((): boolean => {
        if (!isPurviewIntegration || !isInitialized) return false;
        const fieldDefs = getIntegrationFields(integrationType);
        if (!Array.isArray(fieldDefs) || fieldDefs.length === 0) return false;
        return fieldDefs.every((field) => {
            const val = initialValues[field]?.trim();
            return !!val;
        });
    }, [isPurviewIntegration, isInitialized, initialValues, getIntegrationFields, integrationType]);

    // Toggle Purview integration info visibility: show form until configured; hide after
    useEffect(() => {
        if (isPurviewIntegration) {
            setShowPurviewInfo(!isPurviewFullyConfiguredOnServer);
        }
    }, [isPurviewIntegration, isPurviewFullyConfiguredOnServer]);

    useEffect(() => {
        const loadData = async (): Promise<void> => {
            try {
                await Promise.all([
                    fetchConfiguredIntegrations(),
                    fetchStorageKeys(),
                ]);
                setIsInitialized(true);

                if (connectorType && hasHealthEndpoint) {
                    fetchConnectorHealth(connectorType);
                }

                if (connectorType === 'slack') {
                    const manifest = await fetchConnectorManifest('slack');
                    setManifestJson(manifest);
                }

            } catch (e) {
                captureErrorForSentry(e, 'IntegrationConfigPage.loadData');
            }
        };
        loadData();
    }, [
        fetchConfiguredIntegrations,
        fetchStorageKeys,
        connectorType,
        hasHealthEndpoint,
        fetchConnectorHealth,
        fetchConnectorManifest,
    ]);

    // Fetch Purview labels once configuration is loaded
    useEffect(() => {
        if (!isPurviewIntegration || !isInitialized) return;

        const config = settingsStore.configuredIntegrations.purview;
        const fieldDefs = settingsStore.getIntegrationFields('purview');
        const allFieldsPresent = config
            && Array.isArray(fieldDefs)
            && fieldDefs.length > 0
            && fieldDefs.every((f) => {
                const val = config[f]?.trim();
                return !!val;
            });

        if (allFieldsPresent) {
            fetchPurviewLabels();
        }
    }, [isPurviewIntegration, isInitialized, settingsStore, fetchPurviewLabels]);

    useEffect(() => {
        if (isInitialized && integrationType) {
            const loadedValues = getIntegrationValues(integrationType);
            const sanitized: Record<string, string> = {};
            Object.entries(loadedValues).forEach(([key, value]) => {
                sanitized[key] = value?.trim() === '*' ? '' : value;
            });
            setValues(sanitized);
            setInitialValues(sanitized);
        }
    }, [isInitialized, integrationType, getIntegrationValues]);

    const fields = useMemo(
        () => (isInitialized ? getIntegrationFields(integrationType) : []),
        [isInitialized, getIntegrationFields, integrationType],
    );
    const isFileUpload = fields === 'file';

    const handleFieldChange = (fieldName: string, value: string): void => {
        setValues((prev) => ({
            ...prev,
            [fieldName]: value,
        }));
    };

    const handleClearField = (fieldName: string): void => {
        setValues((prev) => ({
            ...prev,
            [fieldName]: '',
        }));
    };

    const handleBack = (): void => {
        navigate(AppRoutes.settings);
    };

    const handleCancel = (): void => {
        setValues(initialValues);
        setJsonFileName('');
    };

    const handleSave = useCallback(async (): Promise<void> => {
        if (!integrationType) return;

        setIsSaving(true);
        const success = await saveIntegration(integrationType, values);

        if (success) {
            message.success('Integration saved successfully');
            if (connectorType && hasHealthEndpoint) {
                await fetchConnectorHealth(connectorType);
            }
            setInitialValues(values);
            setIsSaving(false);
            if (isPurviewIntegration) {
                await syncPurviewLabels();
            }
        } else {
            setIsSaving(false);
            message.error('Failed to save integration');
        }
    }, [
        integrationType,
        values,
        saveIntegration,
        connectorType,
        hasHealthEndpoint,
        fetchConnectorHealth,
        isPurviewIntegration,
        syncPurviewLabels,
    ]);

    const handleReset = useCallback(async (): Promise<void> => {
        if (!integrationType) return;

        const emptyValues: Record<string, string> = {};
        if (Array.isArray(fields)) {
            fields.forEach((field) => {
                emptyValues[field] = '';
            });
        }

        setIsResetting(true);
        const success = await saveIntegration(integrationType, emptyValues);

        if (success) {
            message.success('Integration settings have been reset');
            setValues(emptyValues);
            setInitialValues(emptyValues);
            setJsonFileName('');
            if (connectorType && hasHealthEndpoint) {
                await fetchConnectorHealth(connectorType);
            }
            // Clear Purview labels so the UI returns to the unconfigured state
            if (isPurviewIntegration) {
                settingsStore.purviewLabels = [];
            }
            setIsResetting(false);
        } else {
            setIsResetting(false);
            message.error('Failed to reset integration settings');
        }
    }, [
        integrationType,
        fields,
        saveIntegration,
        connectorType,
        hasHealthEndpoint,
        fetchConnectorHealth,
        isPurviewIntegration,
        settingsStore,
    ]);

    const hasAllRequiredFields = (): boolean => {
        if (isFileUpload) {
            return Object.keys(values).length > 0;
        }
        if (Array.isArray(fields)) {
            return fields.every((field) => values[field]?.trim());
        }
        return false;
    };

    const hasChanges = useMemo((): boolean => {
        if (isFileUpload) {
            return jsonFileName !== '' || JSON.stringify(values) !== JSON.stringify(initialValues);
        }
        const allKeys = new Set([...Object.keys(values), ...Object.keys(initialValues)]);
        return Array.from(allKeys).some((key) => (values[key] || '') !== (initialValues[key] || ''));
    }, [values, initialValues, isFileUpload, jsonFileName]);

    const uploadProps = {
        beforeUpload: (file): boolean => {
            const isJSON = file.type === 'application/json';
            if (!isJSON) {
                message.error(`${file.name} is not a JSON file`);
            }
            return isJSON;
        },
        onChange: async (info): Promise<void> => {
            try {
                const file = info.fileList[0];
                if (file?.originFileObj) {
                    const extractedObject = await parseJsonFile(file.originFileObj);
                    setJsonFileName(file.name);
                    setValues(extractedObject as Record<string, string>);
                }
            } catch {
                message.error('Could not extract data from JSON');
            }
        },
        showUploadList: false,
        accept: 'application/json',
    };

    const handleRemoveJson = (): void => {
        setJsonFileName('');
        setValues({});
    };

    const handleWizardComplete = useCallback(async (credentials: SlackCredentials): Promise<void> => {
        const newValues = {
            clientId: credentials.clientId,
            clientSecret: credentials.clientSecret,
            signingSecret: credentials.signingSecret,
            botToken: credentials.botToken,
            userToken: credentials.userToken,
        };
        setValues(newValues);

        setIsSaving(true);
        const success = await saveIntegration(integrationType, newValues);

        if (success) {
            const successMsg = i18n.t('settings.slackWizard.configurationSaved')
                || 'Slack connector configured successfully';
            message.success(successMsg);
            if (connectorType && hasHealthEndpoint) {
                await fetchConnectorHealth(connectorType);
            }
        } else {
            message.error('Failed to save configuration');
        }
        setIsSaving(false);
    }, [integrationType, saveIntegration, connectorType, hasHealthEndpoint, fetchConnectorHealth]);

    const handleSharePointWizardComplete = useCallback(async (credentials: SharePointCredentials): Promise<void> => {
        const newValues = {
            site: credentials.site,
            clientId: credentials.clientId,
            realm: credentials.realm,
        };
        setValues(newValues);

        setIsSaving(true);
        const success = await saveIntegration(integrationType, newValues);

        if (success) {
            const successMsg = i18n.t('settings.sharepointWizard.configurationSaved')
                || 'SharePoint storage configured successfully';
            message.success(successMsg);
            setInitialValues(newValues);
        } else {
            message.error('Failed to save configuration');
        }
        setIsSaving(false);
    }, [integrationType, saveIntegration]);

    const isSlackIntegration = integrationType === 'slack';
    const isSharepointIntegration = integrationType === 'sharepoint';
    const organizationId = organizationsStore.currentOrganizationId || undefined;

    const isSlackConfigured = useMemo((): boolean => {
        if (!isSlackIntegration || !isInitialized) return true;
        return Object.values(values).some(isRealValue);
    }, [isSlackIntegration, isInitialized, values]);

    if (isLoading || !isInitialized) {
        return (
            <Spinner spinning screenCentered fullHeight>
                <div className={styles.configPage} />
            </Spinner>
        );
    }

    const showPurviewDetails = (!isPurviewIntegration || !isPurviewFullyConfiguredOnServer || showPurviewInfo);

    const getTitle = (): string => {
        if (isPurviewIntegration && isPurviewFullyConfiguredOnServer && !showPurviewDetails) {
            return 'Microsoft Purview';
        }
        const titleKey = `${nameSpace}.${integrationType}.title`;
        return i18n.t(titleKey) || integrationType || 'Integration';
    };

    const renderIntegrationBreadcrumb = (actions?: React.ReactNode): React.ReactNode => (
        <div className={styles.titleRow}>
            <h2 className={styles.title}>
                {integrationMeta && (
                    <img
                        src={integrationMeta.logo}
                        alt=""
                        className={styles.titleIcon}
                        aria-hidden="true"
                    />
                )}
                {integrationMeta && (
                    <>
                        <button
                            type="button"
                            className={styles.titleCategoryLink}
                            onClick={handleBack}
                        >
                            {integrationMeta.category}
                        </button>
                        <span className={styles.titleSeparator}>{'\u2192'}</span>
                    </>
                )}
                <span>{getTitle()}</span>
            </h2>
            {actions}
        </div>
    );

    const handleRefreshHealth = (): void => {
        if (connectorType) {
            fetchConnectorHealth(connectorType);
        }
    };

    const renderHealthStatus = (): React.ReactNode => {
        if (!hasHealthEndpoint || !healthStatus) return null;

        const isRefreshing = healthStatus.isLoading;
        const hasBadStatus = healthStatus.isReachable === false || healthStatus.isConfigured === false;

        const renderTag = (): React.ReactNode => {
            if (isRefreshing) {
                return (
                    <Tag icon={<SyncOutlined spin />} color="processing">
                        Checking connector status...
                    </Tag>
                );
            }

            if (healthStatus.isReachable === false) {
                return (
                    <Tag icon={<ExclamationCircleOutlined />} color="warning">
                        Connector service unreachable
                    </Tag>
                );
            }

            if (healthStatus.isConfigured === true) {
                return (
                    <Tag icon={<CheckCircleOutlined />} color="success">
                        Connector active
                    </Tag>
                );
            }

            if (healthStatus.isConfigured === false) {
                return (
                    <Tag icon={<CloseCircleOutlined />} color="default">
                        Connector not configured
                    </Tag>
                );
            }

            return null;
        };

        return (
            <div className={styles.healthStatusContainer}>
                {renderTag()}
                {hasBadStatus && (
                    <Button
                        type="text"
                        size="small"
                        icon={<ReloadOutlined spin={isRefreshing} />}
                        onClick={handleRefreshHealth}
                        disabled={isRefreshing}
                        className={styles.refreshButton}
                        aria-label="Refresh connector status"
                    />
                )}
            </div>
        );
    };

    /** Render the labels list, a loader, or the empty-state alert */
    const renderPurviewLabelsContent = (): React.ReactNode => {
        if (isPurviewLabelsLoading) {
            return <Spinner spinning />;
        }

        const hasLabels = purviewLabels && purviewLabels.length > 0;
        if (hasLabels) {
            return (
                <div className={styles.purviewLabelsList}>
                    {purviewLabels.map((label) => (
                        <div key={label.guid} className={styles.purviewLabelRow}>
                            <div className={styles.purviewLabelMain}>
                                <span className={styles.purviewLabelName}>{label.name || 'Unnamed label'}</span>
                                {label.description && (
                                    <span className={styles.purviewLabelDescription}>{label.description}</span>
                                )}
                            </div>
                            <Tag className={styles.purviewGuidTag}>{label.guid}</Tag>
                        </div>
                    ))}
                </div>
            );
        }

        return (
            <Alert
                type="info"
                showIcon
                message="No Purview labels available"
                description="Rescan to fetch the latest taxonomy from Microsoft Purview."
            />
        );
    };

    const renderPurviewLabelsSection = (): React.ReactNode => {
        if (!isPurviewIntegration || !isPurviewFullyConfiguredOnServer) return null;

        const hasLabels = purviewLabels && purviewLabels.length > 0;

        return (
            <div className={styles.purviewLabelsBlock}>
                <div className={styles.purviewLabelsHeader}>
                    <span className={styles.purviewLabelsSummary}>
                        {hasLabels
                            ? `Imported ${purviewLabels.length} labels`
                            : 'No Purview labels imported yet'}
                    </span>
                    <Button
                        type="primary"
                        onClick={async (): Promise<void> => {
                            const success = await syncPurviewLabels();
                            if (success) {
                                message.success('Purview taxonomy rescanned');
                            } else {
                                message.error('Failed to rescan Purview taxonomy');
                            }
                        }}
                        loading={isPurviewRescanning}
                        icon={<ReloadOutlined />}
                        className={styles.purviewRescanButton}
                    >
                        {i18n.t('settings.purview.rescan') || 'Rescan Labels'}
                    </Button>
                </div>
                {renderPurviewLabelsContent()}
            </div>
        );
    };

    // Show inline wizard for SharePoint (certificate-based auth)
    if (isSharepointIntegration) {
        const sharepointDefaults: SharePointCredentials = {
            site: values.site || '',
            clientId: values.clientId || '',
            realm: values.realm || '',
        };

        return (
            <Spinner spinning={isLoading || !isInitialized || isSaving} fullHeight>
                <div className={styles.configPage}>
                    <TabTitle title={`Configure ${getTitle()}`} />
                    <div className={styles.wizardLayout}>
                        {renderIntegrationBreadcrumb()}
                        <SharePointInlineWizard
                            onComplete={handleSharePointWizardComplete}
                            onBack={handleBack}
                            organizationId={organizationId}
                            defaultValues={sharepointDefaults}
                        />
                    </div>
                </div>
            </Spinner>
        );
    }

    // Show inline wizard for unconfigured Slack
    if (isSlackIntegration && !isSlackConfigured) {
        return (
            <Spinner spinning={isLoading || !isInitialized || isSaving || !manifestJson} fullHeight>
                <div className={styles.configPage}>
                    <TabTitle title={`Configure ${getTitle()}`} />
                    <div className={styles.wizardLayout}>
                        {renderIntegrationBreadcrumb()}
                        <SlackInlineWizard
                            onComplete={handleWizardComplete}
                            onBack={handleBack}
                            manifestJson={manifestJson ?? undefined}
                        />
                    </div>
                </div>
            </Spinner>
        );
    }

    const renderConfigAndLabels = (): React.ReactNode => (
        <div className={styles.formContainer}>
            {showPurviewDetails && (
                <>
                    {isFileUpload ? (
                        <div className={styles.fileUploadSection}>
                            <p className={styles.uploadDescription}>
                                Upload your JSON configuration file to configure this integration.
                            </p>
                            {!jsonFileName ? (
                                <Upload {...uploadProps}>
                                    <Button type="primary" size="large">
                                        Upload JSON Configuration
                                    </Button>
                                </Upload>
                            ) : (
                                <div className={styles.fileChip}>
                                    <span className={styles.fileName}>{jsonFileName}</span>
                                    <CloseOutlined
                                        className={styles.removeIcon}
                                        onClick={handleRemoveJson}
                                    />
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className={styles.fieldsSection}>
                            {Array.isArray(fields) && fields.map((fieldName) => {
                                const labelKey = `${nameSpace}.${integrationType}.${fieldName}`;
                                const label = i18n.t(labelKey) || fieldName;
                                const isSecret = fieldName.toLowerCase().includes('secret')
                                    || fieldName.toLowerCase().includes('token');

                                return (
                                    <div key={fieldName} className={styles.fieldRow}>
                                        <label className={styles.fieldLabel}>
                                            {label}
                                        </label>
                                        <div className={styles.fieldInputContainer}>
                                            {isSecret ? (
                                                <Password
                                                    value={values[fieldName] || ''}
                                                    onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                                                    placeholder={`Enter ${label}`}
                                                    size="large"
                                                />
                                            ) : (
                                                <Input
                                                    value={values[fieldName] || ''}
                                                    onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                                                    placeholder={`Enter ${label}`}
                                                    size="large"
                                                />
                                            )}
                                            <Button
                                                type="link"
                                                onClick={() => handleClearField(fieldName)}
                                            >
                                                Clear
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className={styles.actions}>
                        <div className={styles.actionsLeft}>
                            <Button
                                onClick={handleSave}
                                loading={isSaving}
                                disabled={
                                    !hasAllRequiredFields()
                                    || !hasChanges
                                }
                                className={styles.saveButton}
                            >
                                Save Configuration
                            </Button>
                            <Button
                                type="text"
                                onClick={handleCancel}
                                className={styles.cancelButton}
                            >
                                Cancel
                            </Button>
                        </div>
                        <Popconfirm
                            title="Reset Integration"
                            description={
                                'This will clear all settings for this integration. '
                                + 'Are you sure you want to continue?'
                            }
                            onConfirm={handleReset}
                            okText="Reset"
                            cancelText="Cancel"
                            okButtonProps={{ danger: true }}
                            icon={<WarningOutlined style={{ color: '#faad14' }} />}
                            placement="topRight"
                        >
                            <Button
                                danger
                                loading={isResetting}
                                className={styles.resetButton}
                            >
                                Reset
                            </Button>
                        </Popconfirm>
                    </div>
                </>
            )}
            {renderPurviewLabelsSection()}
        </div>
    );

    return (
        <Spinner spinning={isLoading || !isInitialized} fullHeight>
            <div className={styles.configPage}>
                <TabTitle title={`Configure ${getTitle()}`} />

                <div className={styles.content}>
                    {renderIntegrationBreadcrumb(
                        <div className={styles.titleActions}>
                            {isPurviewIntegration && isPurviewFullyConfiguredOnServer && (
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={() => setShowPurviewInfo((prev) => !prev)}
                                    className={styles.infoToggle}
                                >
                                    {showPurviewInfo ? 'Hide integration info' : 'See integration info'}
                                </Button>
                            )}
                            {renderHealthStatus()}
                        </div>,
                    )}

                    {(showPurviewDetails || !isPurviewIntegration)
                        ? renderConfigAndLabels()
                        : renderPurviewLabelsSection()}
                </div>
            </div>
        </Spinner>
    );
});

export default IntegrationConfigPage;
